import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Types for aggregated metrics from RPC
interface SalespersonMetric {
  user_id: string;
  user_name: string;
  total_amount: number;
  deals_count: number;
}

interface SourceMetric {
  source: string;
  amount: number;
  count: number;
}

interface ChannelMetric {
  channel: string;
  amount: number;
  count: number;
}

export interface SalesDashboardMetrics {
  total_sales_amount: number;
  total_deals_count: number;
  sales_by_salesperson: SalespersonMetric[];
  sales_by_source: SourceMetric[];
  sales_by_channel: ChannelMetric[];
}

// Types for deal details from RPC
export interface SalesDealDetail {
  id: string;
  contact_first_name: string | null;
  contact_last_name: string | null;
  stage: string;
  closed_date: string;
  created_at: string;
  deals_source: string | null;
  deals_channel: string | null;
  deal_status: string | null;
  sales_consultant: string | null;
  effective_amount: number;
  consultant_name: string;
  sdr_name: string | null;
}

/**
 * Hook para buscar métricas agregadas do dashboard de vendas
 * Usa RPC PostgreSQL para agregação no servidor (ultra-rápido)
 */
export function useAdminSalesMetrics(
  dateRange: { from: Date | null; to: Date | null } | null,
  businessUnit: string = 'all'
) {
  return useQuery({
    queryKey: [
      'admin-sales-metrics',
      dateRange?.from?.toISOString(),
      dateRange?.to?.toISOString(),
      businessUnit,
    ],
    queryFn: async (): Promise<SalesDashboardMetrics> => {
      const { data, error } = await supabase.rpc('get_sales_dashboard_metrics', {
        date_from: dateRange?.from?.toISOString() || null,
        date_to: dateRange?.to?.toISOString() || null,
        business_unit_filter: businessUnit === 'all' ? null : businessUnit,
      });

      if (error) {
        console.error('[useAdminSalesMetrics] RPC error:', error);
        throw error;
      }

      return data as unknown as SalesDashboardMetrics;
    },
    enabled: !!dateRange?.from && !!dateRange?.to,
    staleTime: 1000 * 60 * 5, // 5 minutos cache
    gcTime: 1000 * 60 * 10, // 10 minutos garbage collect
  });
}

/**
 * Hook para buscar detalhes de deals sob demanda (drill-down)
 * Usado ao clicar em barras de origem/canal ou expandir vendedor
 */
export function useSalesDealsDetail(
  dateRange: { from: Date | null; to: Date | null } | null,
  businessUnit: string = 'all',
  filterType: 'source' | 'channel' | 'salesperson' | null,
  filterValue: string | null
) {
  return useQuery({
    queryKey: [
      'sales-deals-detail',
      dateRange?.from?.toISOString(),
      dateRange?.to?.toISOString(),
      businessUnit,
      filterType,
      filterValue,
    ],
    queryFn: async (): Promise<SalesDealDetail[]> => {
      const { data, error } = await supabase.rpc('get_sales_deals_detail', {
        date_from: dateRange?.from?.toISOString() || null,
        date_to: dateRange?.to?.toISOString() || null,
        business_unit_filter: businessUnit === 'all' ? null : businessUnit,
        filter_type: filterType,
        filter_value: filterValue,
      });

      if (error) {
        console.error('[useSalesDealsDetail] RPC error:', error);
        throw error;
      }

      return (data as unknown as SalesDealDetail[]) || [];
    },
    enabled: !!filterType && !!filterValue && !!dateRange?.from && !!dateRange?.to,
    staleTime: 1000 * 60 * 2, // 2 minutos cache
    gcTime: 1000 * 60 * 5,
  });
}
